/**
 * Persistent filter collapse/expand state
 * Maintains filter accordion states across filter updates and page reloads
 */
(function() {
  'use strict';

  const STORAGE_KEY = 'collection_filter_states';
  
  // Get current page identifier
  function getPageKey() {
    return window.location.pathname;
  }

  // Load all states from localStorage
  function loadStates() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  }

  // Save all states to localStorage
  function saveStates(states) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(states));
    } catch (e) {
      console.warn('Could not save filter states');
    }
  }

  // Get current filter states
  function getCurrentStates() {
    const states = {};
    const details = document.querySelectorAll('.filter-group details[data-collapsible]');
    
    details.forEach((detail, index) => {
      const heading = detail.querySelector('.filter-group__heading span');
      if (heading) {
        const label = heading.textContent.trim();
        states[label] = detail.hasAttribute('open');
      }
    });
    
    return states;
  }

  // Apply saved states to filters
  function applyStates(savedStates) {
    if (!savedStates || Object.keys(savedStates).length === 0) return;
    
    const details = document.querySelectorAll('.filter-group details[data-collapsible]');
    
    details.forEach((detail) => {
      const heading = detail.querySelector('.filter-group__heading span');
      if (heading) {
        const label = heading.textContent.trim();
        
        if (label in savedStates) {
          const shouldBeOpen = savedStates[label];
          const isCurrentlyOpen = detail.hasAttribute('open');
          
          if (shouldBeOpen !== isCurrentlyOpen) {
            const body = detail.querySelector('[data-collapsible-body]');
            
            if (shouldBeOpen) {
              detail.setAttribute('open', 'true');
              if (body) body.style.height = 'auto';
            } else {
              detail.removeAttribute('open');
              if (body) body.style.height = '';
            }
          }
        }
      }
    });
  }

  // Save current page states
  function saveCurrentStates() {
    const allStates = loadStates();
    const pageKey = getPageKey();
    const currentStates = getCurrentStates();
    
    allStates[pageKey] = currentStates;
    saveStates(allStates);
  }

  // Restore states for current page
  function restoreStates() {
    const allStates = loadStates();
    const pageKey = getPageKey();
    const savedStates = allStates[pageKey];
    
    if (savedStates) {
      applyStates(savedStates);
    }
  }

  // Attach event listeners to track state changes
  function attachListeners() {
    const details = document.querySelectorAll('.filter-group details[data-collapsible]');
    
    details.forEach((detail) => {
      // Remove old listener if exists
      detail.removeEventListener('toggle', saveCurrentStates);
      // Add new listener
      detail.addEventListener('toggle', saveCurrentStates);
    });
  }

  // Initialize - restore and setup listeners
  function init() {
    restoreStates();
    attachListeners();
  }

  // Observer to watch for sidebar content changes
  function setupObserver() {
    const sidebar = document.querySelector('[data-collection-sidebar]');
    if (!sidebar) return;

    const observer = new MutationObserver((mutations) => {
      // Check if filter groups were modified
      const filterGroupsChanged = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          return node.nodeType === 1 && (
            node.classList?.contains('filter-group') ||
            node.querySelector?.('.filter-group')
          );
        });
      });

      if (filterGroupsChanged) {
        // Wait a tick for DOM to settle
        setTimeout(() => {
          restoreStates();
          attachListeners();
        }, 50);
      }
    });

    observer.observe(sidebar, {
      childList: true,
      subtree: true
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      setupObserver();
    });
  } else {
    init();
    setupObserver();
  }

  // Also restore after browser back/forward navigation
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      setTimeout(init, 100);
    }
  });
})();
